import React, { useState } from 'react';
import { ExternalLink, Mail, Music, ShieldCheck, ShoppingBag, Sparkles } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import AudioPlayer from '../components/AudioPlayer';
import MerchOrderModal from '../components/MerchOrderModal';
import { albums, appleMusicUrl, brandAssets, contactEmail, facebookPageUrl, instagramPageUrl, merchItems, spotifyArtistUrl, youtubeMusicUrl } from '../data/siteData';

const Home = () => {
  const [selectedMerchItem, setSelectedMerchItem] = useState(null);
  const staticOaths = albums.find((album) => album.id === 'static-oaths');

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <div className="min-h-screen">
      <section className="hero-section" data-testid="hero-section">
        <img src={brandAssets.heroBackground} alt="Blue-lit metal concert" className="hero-background-image" />
        <div className="hero-overlay" />

        <div className="container hero-grid is-visible">
          <div className="hero-copy">
            <div className="hero-status" data-testid="hero-release-badge">
              <Sparkles className="h-4 w-4" />
              Statìc Oäths out May 29 • 9 tracks
            </div>
            <h1 className="hero-title" data-testid="hero-title">STATÌC RÎFT</h1>
            <p className="hero-subtitle" data-testid="hero-label">Full Gauge Records</p>
            <p className="hero-genre">Groove Metal / Nu-Metal built for volume, movement, and impact.</p>

            <div className="hero-actions">
              <Button
                onClick={() => scrollToSection('music')}
                className="cta-button-primary"
                size="lg"
                data-testid="hero-listen-button"
              >
                <Music className="mr-2 h-5 w-5" />
                Hear Statìc Oäths
              </Button>
              <Button
                onClick={() => scrollToSection('merch')}
                variant="outline"
                className="cta-button-secondary"
                size="lg"
                data-testid="hero-merch-button"
              >
                <ShoppingBag className="mr-2 h-5 w-5" />
                Shop Merch
              </Button>
            </div>

            <div className="hero-metrics" data-testid="hero-metrics">
              <span><strong>02</strong> releases</span>
              <span><strong>16</strong> listed tracks</span>
              <span><strong>01</strong> official label</span>
            </div>
          </div>

          <div className="hero-album-card" data-testid="hero-album-card">
            <img src={brandAssets.staticOathsCover} alt="Statìc Oäths album cover" className="hero-album-art" />
            <div className="hero-album-meta">
              <span>New Release</span>
              <strong>Statìc Oäths</strong>
            </div>
          </div>
        </div>
      </section>

      <section id="about" className="section-padding section-band about-band" data-testid="about-section">
        <div className="container split-layout">
          <div>
            <p className="section-kicker">Managed by Chase Tucker</p>
            <h2 className="section-title">THE RIFT</h2>
            <div className="section-content space-y-4" data-testid="about-bio-copy">
              <p className="bio-headline">
                Statìc Rîft Shatters the Silence with New Album Statìc Oäths
              </p>
              <p>
                Lock your doors and crank the volume. Statìc Rîft is back to tear down the walls with their lethal new record, Statìc Oäths.
              </p>
              <p>
                Fusing the technical precision of classic thrash metal with the raw, bruising weight of modern hard rock, Statìc Oäths is an absolute sonic assault. From the first downbeat, the album unleashes a relentless barrage of razor-sharp guitar riffs, blistering double-bass drumming, and ferocious vocals that refuse to compromise.
              </p>
              <p className="bio-closing-line">
                The title itself captures the chaotic energy of the music: &quot;Statìc&quot; for the white-noise overload of a world in decay, and &quot;Oäths&quot; for the blood-bound pacts made in the underground. Packed with skull-crushing breakdowns and anthemic, stadium-ready hooks, Statìc Oäths isn’t just a heavy metal album—it’s a declaration of war against the mundane.
              </p>
            </div>
          </div>
          <div className="about-image-wrapper">
            <video
              src={brandAssets.aboutVideo}
              className="about-image"
              autoPlay
              loop
              muted
              playsInline
              data-testid="about-video"
            />
          </div>
        </div>
      </section>

      <section id="music" className="section-padding section-band music-band" data-testid="music-section">
        <div className="container">
          <div className="section-heading-row">
            <div>
              <p className="section-kicker">Music Terminal</p>
              <h2 className="section-title">MUSIC</h2>
            </div>
            <a
              href={spotifyArtistUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="music-platform-button"
              data-testid="spotify-artist-link"
            >
              <Music className="mr-2 h-5 w-5" />
              Follow on Spotify
              <ExternalLink className="ml-2 h-4 w-4" />
            </a>
          </div>
          <div className="listen-everywhere-strip" data-testid="listen-everywhere-strip">
            <span>Listen everywhere</span>
            <a href={spotifyArtistUrl} target="_blank" rel="noopener noreferrer" data-testid="listen-spotify-link">Spotify</a>
            <a href={appleMusicUrl} target="_blank" rel="noopener noreferrer" data-testid="listen-apple-music-link">Apple Music</a>
            <a href={youtubeMusicUrl} target="_blank" rel="noopener noreferrer" data-testid="listen-youtube-music-link">YouTube Music</a>
            <a href={instagramPageUrl} target="_blank" rel="noopener noreferrer" data-testid="listen-instagram-link">Instagram</a>
          </div>
          <AudioPlayer />
        </div>
      </section>

      <section id="upcoming" className="section-padding release-band" data-testid="static-oaths-section">
        <div className="container release-grid">
          <div className="release-copy">
            <p className="section-kicker">Released May 29</p>
            <h2 className="section-title">STATÌC OÄTHS</h2>
            <p className="release-description">
              The new Statìc Rîft album is live with 9 tracks, direct Spotify access, and cover art displayed in release order.
            </p>
            <div className="release-stats" data-testid="static-oaths-release-info">
              <span><strong>May 29</strong> Release Date</span>
              <span><strong>9</strong> Tracks</span>
              <span><strong>Full Gauge</strong> Records</span>
            </div>
            <Button onClick={() => scrollToSection('music')} className="cta-button-primary" data-testid="static-oaths-listen-button">
              <Music className="mr-2 h-5 w-5" />
              Open Album Player
            </Button>
          </div>
          <div className="release-track-covers" data-testid="static-oaths-cover-grid">
            {staticOaths.tracks.map((track, index) => (
              <a
                key={track.title}
                href={track.spotifyUrl}
                target="_blank"
                rel="noopener noreferrer"
                className={`release-cover-tile tile-${index}`}
                data-testid={`static-oaths-cover-${index + 1}`}
              >
                <img src={track.cover} alt={`${track.title} cover art`} />
                <span>{String(index + 1).padStart(2, '0')} / {track.title}</span>
              </a>
            ))}
          </div>
        </div>
      </section>

      <section id="merch" className="section-padding section-band merch-band" data-testid="merch-section">
        <div className="container">
          <div className="section-heading-row">
            <div>
              <p className="section-kicker">Store</p>
              <h2 className="section-title">MERCH</h2>
            </div>
            <p className="section-note" data-testid="shipping-policy">USPS standard rates • Free shipping over $40</p>
          </div>
          <div className="merch-grid">
            {merchItems.map((item, index) => (
              <Card
                key={item.id}
                className={`merch-card group merch-card-${index}`}
                onClick={() => setSelectedMerchItem(item)}
                data-testid={`merch-item-${item.id}`}
                role="button"
                tabIndex={0}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ' ') setSelectedMerchItem(item);
                }}
              >
                <div className="merch-image-wrapper">
                  <img src={item.image} alt={item.name} className="merch-image" />
                  <div className="merch-overlay">
                    <Button className="cta-button-primary" data-testid={`merch-order-${item.id}-button`}>
                      Order Now
                    </Button>
                  </div>
                </div>
                <div className="merch-body">
                  <h3 className="merch-title">{item.name}</h3>
                  <p className="merch-price" data-testid={`merch-price-${item.id}`}>{item.price}</p>
                </div>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {selectedMerchItem && (
        <MerchOrderModal
          item={selectedMerchItem}
          onClose={() => setSelectedMerchItem(null)}
        />
      )}

      <section id="contact" className="section-padding contact-band" data-testid="contact-section">
        <div className="container contact-grid">
          <div>
            <p className="section-kicker">Official Contact</p>
            <h2 className="section-title">GET IN TOUCH</h2>
            <p className="section-content">
              For bookings, collaborations, press, and Spotify for Artists verification, use the official Full Gauge Records contact below.
            </p>
            <div className="verification-card" data-testid="spotify-verification-card">
              <ShieldCheck className="h-6 w-6" />
              <span>Managed by Chase Tucker</span>
              <a href={`mailto:${contactEmail}`} className="contact-email-link" data-testid="contact-email-link">
                {contactEmail}
              </a>
            </div>
          </div>

          <div className="contact-form-wrapper">
            <form className="contact-form space-y-6" onSubmit={(e) => {
              e.preventDefault();
              const formData = new FormData(e.target);
              const name = formData.get('name');
              const email = formData.get('email');
              const message = formData.get('message');
              const subject = `Message from ${name} via Statìc Rîft Website`;
              const body = `Name: ${name}%0D%0AEmail: ${email}%0D%0A%0D%0AMessage:%0D%0A${message}`;
              window.location.href = `mailto:${contactEmail}?subject=${encodeURIComponent(subject)}&body=${body}`;
            }} data-testid="contact-form">
              <input
                type="text"
                name="name"
                placeholder="Your Name"
                className="contact-input"
                required
                data-testid="contact-name-input"
              />
              <input
                type="email"
                name="email"
                placeholder="Your Email"
                className="contact-input"
                required
                data-testid="contact-email-input"
              />
              <textarea
                name="message"
                placeholder="Your Message"
                rows="6"
                className="contact-input"
                required
                data-testid="contact-message-input"
              />
              <Button type="submit" className="cta-button-primary w-full" data-testid="contact-submit-button">
                <Mail className="mr-2 h-5 w-5" />
                Send Message
              </Button>
            </form>
          </div>

          <div className="social-strip" data-testid="social-links">
            <a href={spotifyArtistUrl} target="_blank" rel="noopener noreferrer" className="social-link" data-testid="social-spotify-link">Spotify</a>
            <a href={instagramPageUrl} target="_blank" rel="noopener noreferrer" className="social-link" data-testid="social-instagram-link">Instagram</a>
            <a href={facebookPageUrl} target="_blank" rel="noopener noreferrer" className="social-link" data-testid="social-facebook-link">Facebook</a>
            <a href={youtubeMusicUrl} target="_blank" rel="noopener noreferrer" className="social-link" data-testid="social-youtube-music-link">YouTube Music</a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;